export const BLOG_AUTHOR = 'The Curipod Team';

export const BLOG_TITLE = 'The Curipod Blog';

export const BLOG_DESCRIPTION =
  'Discover how teachers are using AI to create more engaging lessons. Get practical Curipod tips, classroom strategies, and the latest research on AI in education.';

export type BlogCategorySlug =
  | 'curipod-in-the-classroom'
  | 'teaching-and-learning'
  | 'impact-and-efficacy'
  | 'reading-and-writing';

export type BlogCategory = {
  slug: BlogCategorySlug;
  name: string;
  description: string;
};

// Slugs are the Webflow category slugs, so /c/blogs/<slug> stays stable.
export const blogCategories: BlogCategory[] = [
  {
    slug: 'curipod-in-the-classroom',
    name: 'Curipod in the Classroom',
    description:
      'Ways to bring teacher-led writing practice into the school day, from the first weeks to the last bell.',
  },
  {
    slug: 'teaching-and-learning',
    name: 'Teaching & Learning',
    description:
      'Classroom strategies for feedback, writing, and learning together — with the teacher still in charge.',
  },
  {
    slug: 'impact-and-efficacy',
    name: 'Impact & Efficacy',
    description:
      'What changes in student writing when feedback is immediate and the class works together.',
  },
  {
    slug: 'reading-and-writing',
    name: 'Reading & Writing',
    description:
      'Premade, curriculum-aligned reading and writing practice, and what changes when classes keep at it.',
  },
];

export type BlogPost = {
  slug: string;
  /** Visible title. The page h1. */
  title: string;
  /** Document title. Often longer than `title`, sometimes the same sentence. */
  seoTitle: string;
  /** Card text and meta description. */
  description: string;
  category: BlogCategorySlug;
  author: string;
  /** CMS created date, YYYY-MM-DD. Posts have no separate publish-date field. */
  publishedAt: string;
  /** Public path, for example `/blog/my-post/cover.png`. */
  cover: string;
  coverAlt: string;
  featured?: boolean;
};

export const blogPosts: BlogPost[] = [
  // posts:start
  {
    slug: 'why-staar-ecrs-score-zero',
    title: 'Why STAAR ECRs Score a Zero, and How to Prevent It',
    seoTitle: 'Why STAAR ECRs Score a Zero, and How to Prevent It',
    description:
      'Why STAAR ECRs score a zero, how to prevent ECR zeros, and what Texas ELA leaders can do this semester to close the writing skill gaps behind them.',
    category: 'reading-and-writing',
    author: 'The Curipod Team',
    publishedAt: '2026-11-03',
    cover: '/blog/why-staar-ecrs-score-zero/cover.png',
    coverAlt:
      'Line chart: the same Texas classes tracked from session 1 to 6, January to June 2026. The share of ECRs scoring 3 or higher out of 5 rises from 40% to 49%, and the share of SCRs earning full credit (2 of 2) rises from 24% to 33%.',
  },
  {
    slug: 'bluebonnet-intervention-activities-tier-2-tier-3',
    title:
      'Bluebonnet Intervention Activities: Using TEKS-Aligned Practice for Tier 2 and Tier 3 Writing',
    seoTitle:
      'Bluebonnet Intervention Activities: Using TEKS-Aligned Practice for Tier 2 and Tier 3 Writing',
    description:
      "How ELA leaders can use Curipod's TEKS-aligned practice for Bluebonnet units in small group, WIN time, intervention and Pausing Points.",
    category: 'reading-and-writing',
    author: 'The Curipod Team',
    publishedAt: '2026-11-03',
    cover: '/blog/bluebonnet-intervention-activities-tier-2-tier-3/cover.png',
    coverAlt:
      "Curipod's TEKS-Aligned Practice page with Bluebonnet and 3rd Grade selected, showing Unit 1, Lesson 1 activities with time, type, when-to-use tags, TEKS, what students do, and why this activity.",
  },
  {
    slug: 'what-is-curipod-premade-curriculum-aligned-writing-practice',
    title:
      'What Is Curipod? Premade, Curriculum-Aligned Writing Practice Built on Authentic Texts',
    seoTitle:
      'What Is Curipod? Premade, Curriculum-Aligned Writing Practice Built on Authentic Texts',
    description:
      'Curipod now offers premade, quality-assured writing practice for grades 3–12, aligned to your curriculum and built on authentic texts by real authors.',
    category: 'reading-and-writing',
    author: 'The Curipod Team',
    publishedAt: '2026-11-03',
    cover: '/blog/what-is-curipod-premade-curriculum-aligned-writing-practice/cover.png',
    coverAlt:
      'What Is Curipod? Premade, Curriculum-Aligned Writing Practice Built on Authentic Texts',
  },
  {
    slug: 'every-student-writing-10-minutes-without-grading-30-more-papers',
    title:
      'How to Get Every Student Writing in 10 Minutes (Without Grading 30 More Papers)',
    seoTitle:
      'Daily Writing Practice K-12: Get Every Student Writing in 10 Minutes | Curipod',
    description:
      "The science-of-reading wave is raising the bar on writing — but grading is the real bottleneck. Here's a high-frequency, low-grading routine that gets every student writing, with AI feedback the teacher reviews instead of authors.",
    category: 'teaching-and-learning',
    author: 'The Curipod Team',
    publishedAt: '2026-04-21',
    cover:
      '/blog/every-student-writing-10-minutes-without-grading-30-more-papers/cover.png',
    coverAlt:
      'Three classroom desks in a row, each with an open notebook, pencil, and laptop showing a speech bubble, with a soft orange clock and floating speech bubbles above — illustrating a 10-minute writing routine.',
  },
  {
    slug: 'post-testing-slump-activities-keep-students-learning',
    title:
      'The Post-Testing Slump Is Real: Activities That Keep Students Learning After State Tests',
    seoTitle:
      'The Post-Testing Slump Is Real: Activities That Keep Students Learning After State Tests | Curipod',
    description:
      "The weeks after state testing don't have to be a countdown to summer. Writing Escape Rooms and whole-class discussions keep students engaged, writing, and building skills that weren't on the test.",
    category: 'teaching-and-learning',
    author: 'The Curipod Team',
    publishedAt: '2026-04-14',
    cover: '/blog/post-testing-slump-activities-keep-students-learning/cover.png',
    coverAlt:
      'The Post-Testing Slump Is Real: Activities That Keep Students Learning After State Tests',
  },
  {
    slug: 'screen-time-laws-edtech-whole-class-tools-survive',
    title:
      'Screen Time Laws Are Coming for EdTech — Why Whole-Class Tools Survive the Backlash',
    seoTitle:
      'Screen Time Laws Are Coming for EdTech — Why Whole-Class Tools Survive the Backlash | Curipod',
    description:
      '16+ states are moving to limit screen time in schools — but the legislation misses a critical distinction between isolating, self-paced tech and teacher-led, whole-class tools that bring students together.',
    category: 'teaching-and-learning',
    author: 'The Curipod Team',
    publishedAt: '2026-04-14',
    cover: '/blog/screen-time-laws-edtech-whole-class-tools-survive/cover.png',
    coverAlt:
      'Screen Time Laws Are Coming for EdTech — Why Whole-Class Tools Survive the Backlash',
  },
  {
    slug: 'ai-feedback-student-writing-teacher-controlled-beats-chatbot',
    title: 'AI Feedback on Student Writing: Why Teacher-Controlled Beats the Chatbot',
    seoTitle:
      'AI Feedback on Student Writing: Why Teacher-Controlled Beats the Chatbot | Curipod',
    description:
      'New research shows hybrid teacher+AI feedback outperforms chatbots and auto-graders. Learn why teacher-controlled, rubric-aligned AI feedback is the safer, more effective approach for student writing.',
    category: 'teaching-and-learning',
    author: 'The Curipod Team',
    publishedAt: '2026-04-08',
    cover: '/blog/ai-feedback-student-writing-teacher-controlled-beats-chatbot/cover.png',
    coverAlt: 'AI Feedback on Student Writing: Why Teacher-Controlled Beats the Chatbot',
  },
  {
    slug: 'year-of-the-sentence-writing-activities-every-student',
    title:
      "It's the Year of the Sentence: Writing Activities That Get Every Student Producing Text",
    seoTitle: 'Writing Activities That Get Every Student Producing Text | Curipod',
    description:
      '2026 is the year of the sentence. Discover whole-class writing activities — like QuickWrites and Writing Escape Rooms — that get every student producing text, including your most reluctant writers.',
    category: 'teaching-and-learning',
    author: 'The Curipod Team',
    publishedAt: '2026-04-08',
    cover: '/blog/year-of-the-sentence-writing-activities-every-student/cover.png',
    coverAlt:
      "It's the Year of the Sentence: Writing Activities That Get Every Student Producing Text",
  },
  {
    slug: 'elevating-core-curriculum-with-curipod',
    title: 'Elevating Core Curriculum with Curipod',
    seoTitle: 'Elevating Core Curriculum with Curipod',
    description:
      "Don't replace your curriculum — elevate it. Curipod transforms existing materials into interactive lessons that spark dialogue, deepen thinking, and boost student engagement.",
    category: 'teaching-and-learning',
    author: 'The Curipod Team',
    publishedAt: '2025-09-24',
    cover: '/blog/elevating-core-curriculum-with-curipod/cover.png',
    coverAlt: 'Elevating Core Curriculum with Curipod',
  },
  {
    slug: 'language-learning-that-sticks-how-curipod-supports-multilingual-students',
    title: 'Language Learning That Sticks: How Curipod Supports Multilingual Students',
    seoTitle:
      'Language Learning That Sticks: How Curipod Empowers Multilingual Students to Thrive',
    description:
      'Discover how Curipod helps multilingual and emergent bilingual students build language skills through real-time feedback, anonymous participation, and lessons in any language.',
    category: 'teaching-and-learning',
    author: 'The Curipod Team',
    publishedAt: '2025-09-17',
    cover:
      '/blog/language-learning-that-sticks-how-curipod-supports-multilingual-students/cover.png',
    coverAlt: 'Language Learning That Sticks: How Curipod Supports Multilingual Students',
  },
  {
    slug:
      'beyond-the-ela-classroom-how-to-improve-writing-instruction-across-the-curriculum',
    title:
      'Beyond the ELA Classroom: How to Improve Writing Instruction Across the Curriculum',
    seoTitle: 'How to Improve Writing Instruction Across the Curriculum',
    description:
      "Writing instruction shouldn't live only in ELA. Learn how Curipod helps schools and districts embed meaningful, feedback-rich writing practice across every subject and grade level.",
    category: 'teaching-and-learning',
    author: 'The Curipod Team',
    publishedAt: '2025-09-16',
    cover:
      '/blog/beyond-the-ela-classroom-how-to-improve-writing-instruction-across-the-curriculum/cover.png',
    coverAlt:
      'Beyond the ELA Classroom: How to Improve Writing Instruction Across the Curriculum',
  },
  {
    slug:
      'from-test-prep-to-lasting-learning-how-to-raise-standardized-test-scores-without-burning-out-teachers-or-students',
    title:
      'From Test Prep to Lasting Learning: How to Raise Standardized Test Scores Without Burning Out Teachers or Students',
    seoTitle: 'Beyond Test Prep: Raising Scores and Supporting Teachers',
    description:
      "See how schools raised STAAR scores double-digit with Curipod's AI-powered, rubric-aligned writing practice — built into everyday lessons, not just test season.",
    category: 'teaching-and-learning',
    author: 'The Curipod Team',
    publishedAt: '2025-09-16',
    cover:
      '/blog/from-test-prep-to-lasting-learning-how-to-raise-standardized-test-scores-without-burning-out-teachers-or-students/cover.png',
    coverAlt:
      'From Test Prep to Lasting Learning: How to Raise Standardized Test Scores Without Burning Out Teachers or Students',
  },
  {
    slug:
      'solving-the-right-problems-how-school-leaders-can-raise-instructional-quality-with-curipod',
    title:
      'Solving the Right Problems: How School Leaders Can Raise Instructional Quality with Curipod',
    seoTitle: 'How School Leaders Can Improve Instructional Quality with Curipod',
    description:
      'Every school leader wants classrooms where students are engaged, challenged, and supported to reach their full potential. But getting there isn’t easy. In conversations with administrators across the country, three challenges stand out: improving instructional quality, boosting test scores without losing authentic learning, and strengthening writing across the curriculum. This first post focuses on the foundation—raising instructional quality—and how schools are making high-quality teaching the easiest thing to do.',
    category: 'teaching-and-learning',
    author: 'The Curipod Team',
    publishedAt: '2025-09-16',
    cover:
      '/blog/solving-the-right-problems-how-school-leaders-can-raise-instructional-quality-with-curipod/cover.png',
    coverAlt:
      'Solving the Right Problems: How School Leaders Can Raise Instructional Quality with Curipod',
  },
  {
    slug: 'cooperative-learning-with-curipod-boosts-student-success',
    title: 'Cooperative Learning with Curipod Boosts Student Success',
    seoTitle: 'Cooperative Learning with Curipod Boosts Student Success',
    description:
      'New research shows Curipod combined with group investigation dramatically boosts mastery and engagement. See how one classroom went from 0% to 89% proficiency.',
    category: 'impact-and-efficacy',
    author: 'The Curipod Team',
    publishedAt: '2025-08-05',
    cover: '/blog/cooperative-learning-with-curipod-boosts-student-success/cover.png',
    coverAlt: 'Cooperative Learning with Curipod Boosts Student Success',
  },
  {
    slug: 'curipod-upskills-new-teachers-and-reduces-planning-time-by-30',
    title: 'Curipod Upskills New Teachers and Reduces Planning Time by 30%',
    seoTitle: 'Curipod Upskills New Teachers and Reduces Planning Time by 30%',
    description:
      'A 2025 study found that new teachers using Curipod improved lesson quality from 2.6 to 4.3 out of 5, cut planning time by 30%, and gained confidence from day one.',
    category: 'impact-and-efficacy',
    author: 'The Curipod Team',
    publishedAt: '2025-08-05',
    cover:
      '/blog/curipod-upskills-new-teachers-and-reduces-planning-time-by-30/cover.png',
    coverAlt: 'Curipod Upskills New Teachers and Reduces Planning Time by 30%',
  },
  {
    slug: 'instructional-shifts-that-stick',
    title: 'Instructional Shifts That Stick: A Northville Impact Story',
    seoTitle: 'Instructional Shifts That Stick with Curipod in Michigan',
    description: 'How a Middle School Ignited Engagement with Curipod',
    category: 'impact-and-efficacy',
    author: 'The Curipod Team',
    publishedAt: '2025-08-05',
    cover: '/blog/instructional-shifts-that-stick/cover.png',
    coverAlt: 'Instructional Shifts That Stick: A Northville Impact Story',
  },
  {
    slug: '46-percent-caasp-growth-ca',
    title: 'Targeted Writing Practice and Immediate Feedback in CA Boost Achievement 46%',
    seoTitle: '46 percent CAASP Growth in CA',
    description:
      'How a group of veteran teachers in Southern California finally found a tech tool that adds value to their instruction, driving a double-digit increase in student achievement.',
    category: 'impact-and-efficacy',
    author: 'The Curipod Team',
    publishedAt: '2025-08-04',
    cover: '/blog/46-percent-caasp-growth-ca/cover.png',
    coverAlt:
      'Targeted Writing Practice and Immediate Feedback in CA Boost Achievement 46%',
  },
  {
    slug: 'lessons-you-can-create-as-an-esl-ell-teacher-with-curipod',
    title: 'Lessons You Can Create as an ESL/ELL Teacher with Curipod',
    seoTitle: 'Lessons You Can Create as an ESL/ELL Teacher with Curipod',
    description: 'Lesson Topics ESL/ELL Teachers Can Create with Curipod',
    category: 'curipod-in-the-classroom',
    author: 'The Curipod Team',
    publishedAt: '2025-08-01',
    cover: '/blog/lessons-you-can-create-as-an-esl-ell-teacher-with-curipod/cover.png',
    coverAlt: 'Lessons You Can Create as an ESL/ELL Teacher with Curipod',
  },
  {
    slug: '32-percent-growth-in-staar-scores-in-texas',
    title: '32 Percent Growth in STAAR Scores in Texas',
    seoTitle: '32 Percent Growth in STAAR Scores in Texas',
    description:
      "See how one Texas middle school went from 46% to 84% Meets & Masters on STAAR in two years — by embedding Curipod's AI-powered writing practice into everyday instruction.",
    category: 'impact-and-efficacy',
    author: 'The Curipod Team',
    publishedAt: '2025-07-31',
    cover: '/blog/32-percent-growth-in-staar-scores-in-texas/cover.png',
    coverAlt: '32 Percent Growth in STAAR Scores in Texas',
  },
  {
    slug: 'immediate-feedback-loop-raises-achievement-22',
    title: '22% Increase in Meets & Masters on STAAR in Texas',
    seoTitle: '22% Increase in Meets & Masters on STAAR in Texas',
    description:
      'How one teacher found a way to do the impossible—engage every student in writing and provide instant feedback on each writing piece—an approach that spread across 6th-grade classrooms, driving unprecedented achievement.',
    category: 'impact-and-efficacy',
    author: 'The Curipod Team',
    publishedAt: '2025-07-31',
    cover: '/blog/immediate-feedback-loop-raises-achievement-22/cover.png',
    coverAlt: '22% Increase in Meets & Masters on STAAR in Texas',
  },
  {
    slug: 'how-to-bring-the-curipod-buzz-to-your-school',
    title: 'How to Bring the Curipod Buzz to Your School',
    seoTitle: 'How to Bring the Curipod Buzz to Your School',
    description: 'A guide to maximizing impact with smart funding strategies',
    category: 'curipod-in-the-classroom',
    author: 'The Curipod Team',
    publishedAt: '2025-07-28',
    cover: '/blog/how-to-bring-the-curipod-buzz-to-your-school/cover.png',
    coverAlt: 'How to Bring the Curipod Buzz to Your School',
  },
  {
    slug: 'curify-your-core-curriculum',
    title: 'Curify Your Core Curriculum',
    seoTitle: 'Curify Your Core Curriculum',
    description:
      'Curify turns the materials you already trust into interactive, student-centered lessons.',
    category: 'curipod-in-the-classroom',
    author: 'The Curipod Team',
    publishedAt: '2025-06-25',
    cover: '/blog/curify-your-core-curriculum/cover.png',
    coverAlt: 'Curify Your Core Curriculum',
  },
  {
    slug: 'sharing-the-spark-a-guide-to-presenting-curipod-with-confidence',
    title: 'Sharing the Spark: A Guide to Presenting Curipod with Confidence',
    seoTitle: 'Sharing the Spark: A Guide to Presenting Curipod with Confidence',
    description: 'Share Curipod—spark a chain reaction of student engagement.',
    category: 'teaching-and-learning',
    author: 'The Curipod Team',
    publishedAt: '2025-06-24',
    cover:
      '/blog/sharing-the-spark-a-guide-to-presenting-curipod-with-confidence/cover.png',
    coverAlt: 'Sharing the Spark: A Guide to Presenting Curipod with Confidence',
  },
  {
    slug: 'why-feedback-fuels-learning-and-how-curipod-makes-it-easy',
    title: 'Why Feedback Fuels Learning (and How Curipod Makes It Easy)',
    seoTitle: 'Why Feedback Fuels Learning (and How Curipod Makes It Easy)',
    description:
      'Great feedback transforms learning. Curipod makes it easier than ever to build into every lesson.',
    category: 'teaching-and-learning',
    author: 'The Curipod Team',
    publishedAt: '2025-06-05',
    cover: '/blog/why-feedback-fuels-learning-and-how-curipod-makes-it-easy/cover.png',
    coverAlt: 'Why Feedback Fuels Learning (and How Curipod Makes It Easy)',
  },
  {
    slug: 'make-thinking-visible-10-formative-assessments-that-center-student-voice',
    title: 'Make Thinking Visible: 10 Formative Assessments That Center Student Voice',
    seoTitle: 'Make Thinking Visible: 10 Formative Assessments That Center Student Voice',
    description:
      'Formative assessment isn’t about checking boxes—it’s about building better classrooms in real-time.',
    category: 'curipod-in-the-classroom',
    author: 'The Curipod Team',
    publishedAt: '2025-05-23',
    cover:
      '/blog/make-thinking-visible-10-formative-assessments-that-center-student-voice/cover.png',
    coverAlt: 'Make Thinking Visible: 10 Formative Assessments That Center Student Voice',
  },
  {
    slug: 'the-problem-with-ai-lesson-plans-and-how-curipod-is-different',
    title: 'The Problem with AI Lesson Plans—And How Curipod Is Different',
    seoTitle: 'The Problem with AI Lesson Plans—And How Curipod Is Different',
    description:
      'AI-generated lessons often miss the mark—reinforcing outdated, one-size-fits-all teaching. Curipod’s updated lesson generator is grounded in pedagogy and built for real student engagement.',
    category: 'teaching-and-learning',
    author: 'The Curipod Team',
    publishedAt: '2025-05-22',
    cover:
      '/blog/the-problem-with-ai-lesson-plans-and-how-curipod-is-different/cover.png',
    coverAlt: 'The Problem with AI Lesson Plans—And How Curipod Is Different',
  },
  {
    slug:
      'buzz-until-the-last-bell-how-curipod-sparks-curiosity-through-year-end-and-summer-learning',
    title:
      'Buzz Until the Last Bell: How Curipod Sparks Curiosity Through Year-End and Summer Learning',
    seoTitle:
      'Buzz Until the Last Bell: How Curipod Sparks Curiosity Through Year-End and Summer Learning',
    description:
      'The last bell doesn’t have to mean learning fades out. With Curipod, classrooms stay alive with curiosity, connection, and conversation—right up to the final day and even into summer learning.',
    category: 'curipod-in-the-classroom',
    author: 'The Curipod Team',
    publishedAt: '2025-05-21',
    cover:
      '/blog/buzz-until-the-last-bell-how-curipod-sparks-curiosity-through-year-end-and-summer-learning/cover.png',
    coverAlt:
      'Buzz Until the Last Bell: How Curipod Sparks Curiosity Through Year-End and Summer Learning',
  },
  {
    slug: 'designing-for-all-learners-how-curipod-supports-udl-in-the-classroom',
    title: 'Designing for All Learners: UDL in Curipod',
    seoTitle: 'Designing for All Learners: UDL in Curipod',
    description: 'How Curipod uses UDL to spark curiosity, conversation, and connection.',
    category: 'teaching-and-learning',
    author: 'The Curipod Team',
    publishedAt: '2025-05-13',
    cover:
      '/blog/designing-for-all-learners-how-curipod-supports-udl-in-the-classroom/cover.png',
    coverAlt: 'Designing for All Learners: UDL in Curipod',
  },
  {
    slug: 'why-were-betting-on-learning-together',
    title: 'Why We’re Betting on Learning Together',
    seoTitle: 'Why We’re Betting on Learning Together',
    description:
      'A challenge to the “personalized” model and a call for something better.',
    category: 'teaching-and-learning',
    author: 'The Curipod Team',
    publishedAt: '2025-05-09',
    cover: '/blog/why-were-betting-on-learning-together/cover.png',
    coverAlt: "Why We're Betting on Learning Together",
    featured: true,
  },
  // posts:end
];

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** `2025-05-09` → `9 May 2025`. Parsed as a calendar date, not a UTC instant. */
export function formatBlogDate(publishedAt: string): string {
  const [year, month, day] = publishedAt.split('-');
  const monthName = MONTHS[Number(month) - 1];
  if (!year || !monthName || !day) return publishedAt;
  return `${Number(day)} ${monthName} ${year}`;
}

export function blogPostPath(slug: string): string {
  return `/c/blog-posts/${slug}`;
}

export function blogCategoryPath(slug: BlogCategorySlug | string): string {
  return `/c/blogs/${slug}`;
}

export function getBlogCategory(slug: string): BlogCategory | undefined {
  return blogCategories.find(category => category.slug === slug);
}

export function getBlogPost(slug: string): BlogPost {
  const post = blogPosts.find(entry => entry.slug === slug);
  if (!post) {
    throw new Error(`Unknown blog post: ${slug}`);
  }
  return post;
}

export function getFeaturedPost(): BlogPost {
  const featured = blogPosts.find(post => post.featured);
  if (!featured) {
    throw new Error('Blog is missing a featured post');
  }
  return featured;
}

export function postsNewestFirst(): BlogPost[] {
  return [...blogPosts].sort((a, b) => {
    if (a.publishedAt !== b.publishedAt) {
      return a.publishedAt < b.publishedAt ? 1 : -1;
    }
    return a.title.localeCompare(b.title);
  });
}

export function postsInCategory(slug: BlogCategorySlug): BlogPost[] {
  return postsNewestFirst().filter(post => post.category === slug);
}
